import { useCallback, useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "./ErrorNote";

type Allergy = {
  id: string;
  kind: "allergy" | "no_known_allergies";
  substance_text: string;
  category: string | null;
  criticality: "low" | "high" | "unable_to_assess";
  reaction_text: string;
  clinical_status: "active" | "resolved" | "entered_in_error";
  recorded_by_name: string;
};

type State = "none_asked" | "none_known" | "has_allergies";

const CRITICALITY_LABELS: Record<string, string> = {
  high: "High",
  low: "Low",
  unable_to_assess: "Severity not assessed",
};

const CATEGORIES = ["medication", "food", "environment", "biologic"] as const;

/**
 * What the patient reacts to, above everything else on the chart.
 *
 * This sits at the top of the chart and never collapses, because CLN-F-071
 * puts it before any action that prescribes. A doctor should not have to go
 * looking for it, and a doctor who has to click for it will eventually not.
 *
 * Three states, not two. An empty list means either that nobody has asked or
 * that somebody asked and the answer was none, and those are different facts
 * (CLN-F-070). "Not recorded" is drawn as a warning, because an unasked
 * question is an open risk rather than a clean bill.
 *
 * A nurse sees this panel and may write to it. Taking the allergy history is
 * nursing work, and a list only a doctor can start is a list that stays empty.
 * Nothing here filters by the restricted tier: an allergy is never hidden from
 * a treating clinician (ADR-015 point 6), and the server sends the whole list.
 */
export function AllergyBanner({
  patientId,
  canRecord,
}: {
  patientId: string;
  canRecord: boolean;
}) {
  const [items, setItems] = useState<Allergy[]>([]);
  const [state, setState] = useState<State>("none_asked");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: failed } = await api.GET("/portal/patients/{id}/allergies", {
      params: { path: { id: patientId }, header: clinicHeader() },
    });
    setLoading(false);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setItems((data?.items ?? []) as Allergy[]);
    setState((data?.state ?? "none_asked") as State);
  }, [patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const recordNoneKnown = async () => {
    const { error: failed } = await api.POST("/portal/patients/{id}/allergies", {
      params: { path: { id: patientId }, header: clinicHeader() },
      body: { kind: "no_known_allergies" },
    });
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    await load();
  };

  if (loading) return <div className="allergies allergies-quiet">Checking allergies…</div>;

  const tone =
    state === "has_allergies" ? "allergies-alert" : state === "none_known" ? "allergies-clear" : "allergies-unknown";

  // Allergies somebody answered once and later resolved. They are not the
  // current answer, but they are evidence the question was asked.
  const resolved = items.filter(
    (row) => row.kind === "allergy" && row.clinical_status === "resolved",
  );

  return (
    <div className={`allergies ${tone}`}>
      <div className="allergies-head">
        <h2>Allergies</h2>
        {canRecord && !adding && (
          <span className="allergies-actions">
            <button type="button" onClick={() => setAdding(true)}>
              Record an allergy
            </button>
            {state === "none_asked" && (
              <button type="button" onClick={recordNoneKnown}>
                None known
              </button>
            )}
          </span>
        )}
      </div>

      <ErrorNote message={error} />

      {/* A patient whose only allergy was later resolved has no current
          answer, so the amber tone is right: somebody should ask again. But
          saying nobody ever asked would be false, and the resolved row is the
          proof. Show it rather than claim the chart is blank. */}
      {state === "none_asked" && resolved.length === 0 && (
        <p className="allergies-state">
          Not recorded. Nobody has asked this patient yet.
        </p>
      )}

      {state === "none_asked" && resolved.length > 0 && (
        <>
          <p className="allergies-state">No current allergy record. Ask again.</p>
          <ul className="allergies-list">
            {resolved.map((row) => (
              <li key={row.id}>
                <strong>{row.substance_text}</strong>
                <span className="muted"> · resolved</span>
                {row.reaction_text && <span className="muted"> · {row.reaction_text}</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      {state === "none_known" && (
        <p className="allergies-state">No known allergies. Somebody asked, and the answer was none.</p>
      )}

      {state === "has_allergies" && (
        <ul className="allergies-list">
          {items
            // Active only. A resolved allergy listed beside a live one reads
            // as live, which is the wrong way round for this panel to be
            // wrong: it buries the real one in noise.
            .filter((row) => row.kind === "allergy" && row.clinical_status === "active")
            .map((row) => (
              <li key={row.id}>
                <strong>{row.substance_text}</strong>
                {row.criticality === "high" && <span className="allergies-high">High</span>}
                {row.criticality !== "high" && (
                  <span className="muted"> · {CRITICALITY_LABELS[row.criticality]}</span>
                )}
                {row.reaction_text && <span className="muted"> · {row.reaction_text}</span>}
                {row.category && <span className="muted"> · {row.category}</span>}
              </li>
            ))}
        </ul>
      )}

      {state === "has_allergies" && resolved.length > 0 && (
        <p className="muted allergies-resolved">
          Resolved: {resolved.map((row) => row.substance_text).join(", ")}
        </p>
      )}

      {adding && (
        <AllergyForm
          patientId={patientId}
          onDone={() => {
            setAdding(false);
            void load();
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </div>
  );
}

function AllergyForm({
  patientId,
  onDone,
  onCancel,
}: {
  patientId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [substance, setSubstance] = useState("");
  const [category, setCategory] = useState<string>("medication");
  const [criticality, setCriticality] = useState<string>("unable_to_assess");
  const [reaction, setReaction] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const { error: failed } = await api.POST("/portal/patients/{id}/allergies", {
      params: { path: { id: patientId }, header: clinicHeader() },
      body: {
        kind: "allergy",
        substance_text: substance.trim(),
        category: category as (typeof CATEGORIES)[number],
        criticality: criticality as "low" | "high" | "unable_to_assess",
        reaction_text: reaction.trim(),
      },
    });
    setSaving(false);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    onDone();
  };

  return (
    <form className="record-form" onSubmit={submit}>
      <label>
        Substance
        <input
          value={substance}
          onChange={(e) => setSubstance(e.target.value)}
          required
          autoFocus
          placeholder="Penicillin"
        />
      </label>

      <label>
        Category
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>

      <label>
        Severity
        <select value={criticality} onChange={(e) => setCriticality(e.target.value)}>
          <option value="high">High</option>
          <option value="low">Low</option>
          <option value="unable_to_assess">Not assessed</option>
        </select>
      </label>

      <label>
        Reaction <span className="optional">optional</span>
        <input
          value={reaction}
          onChange={(e) => setReaction(e.target.value)}
          placeholder="Rash and swelling"
        />
      </label>

      <ErrorNote message={error} />

      <div className="row-actions">
        <button type="submit" disabled={saving || !substance.trim()}>
          {saving ? "Recording…" : "Record"}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
